import { Link } from 'react-router-dom';
import { Bell, Pencil, Download, Mail } from 'lucide-react';
import { useAuth } from '../../../utils/Auth/AuthProvider';
import type { StudentOverview, StudentParentResponse } from '../../api/types';
import { Button } from '../../components/ui';
import { classLabel } from '../../components/studentRow';
import { capitalize, dayMonth } from './lib/format';
import { formatRoll } from '../../lib/roll';

interface Props {
  overview: StudentOverview;
  /** Jumps to the Cases & Notes tab, where the note form lives. */
  onAddNote?: () => void;
  /** Builds and downloads the weekly parent report PDF. */
  onExportReport?: () => void;
  exporting?: boolean;
  /** Parent / guardian contact, for the guardian line. */
  guardian?: StudentParentResponse | null;
  /** 1-based position of the student's open remediation cycle ("Remediation · cycle 2"). */
  remediationCycle?: number | null;
  /** Opens a notice to this student. */
  onMessageStudent?: () => void;
}

// Artboard `.tg`: 30px pill, 13px / 500, navy-2 on the band; `.hot` is the warm alert tag.
const TAG = 'inline-flex min-h-[30px] items-center gap-[7px] rounded-full bg-navy-2 px-3 text-[13px] font-medium text-mist-fixed';
const ENGAGEMENT_LABEL = { active: 'Active', idle: 'Idle', silent: 'Silent' } as const;
const WELLBEING_LABEL = { ok: 'OK', watch: 'Watch', concern: 'Concern' } as const;
const DOT = { ok: 'bg-success', active: 'bg-success', watch: 'bg-idle', idle: 'bg-idle', concern: 'bg-danger', silent: 'bg-danger' } as const;
const BAND_ICON = 'h-[18px] w-[18px]';

function initials(first: string, last: string, username: string): string {
  const s = `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
  return s || (username?.[0] ?? '?').toUpperCase();
}

function RiskRing({ score, band }: { score: number; band: string }) {
  const v = Number.isFinite(score) ? Math.max(0, Math.min(100, score)) : 0;
  const c = 2 * Math.PI * 33;
  return (
    <div className="flex flex-none items-center gap-3.5 rounded-[22px] bg-navy-2 py-3 pl-3 pr-5">
      <svg width={84} height={84} viewBox="0 0 84 84" role="img" aria-label={`Risk score: ${Math.round(v)}`}>
        <circle cx={42} cy={42} r={33} fill="none" className="stroke-navy-3" strokeWidth={9} />
        <circle cx={42} cy={42} r={33} fill="none" className="stroke-accent" strokeWidth={9} strokeLinecap="round"
          strokeDasharray={`${(c * v) / 100} ${c}`} transform="rotate(-90 42 42)" />
        <text x={42} y={49} textAnchor="middle" className="fill-white font-mono text-[22px] font-semibold">{Math.round(v)}</text>
      </svg>
      <div>
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-on-navy-muted">Risk score</p>
        <p className="mt-1 font-display text-[22px] font-bold capitalize leading-tight">{band}</p>
      </div>
    </div>
  );
}

export default function HeaderBand({ overview, onAddNote, onExportReport, exporting = false, guardian = null, remediationCycle = null, onMessageStudent }: Props) {
  const { role } = useAuth();
  const teacherView = role === 'teacher';
  const { profile, kpis } = overview;
  const name = `${profile.first_name} ${profile.last_name}`.trim() || profile.username;
  const gradeSection = overview.section ? classLabel(profile.school_grade ?? 'Unassigned', overview.section) : (profile.school_grade ?? 'Unassigned');
  const meta = [
    profile.roll_number != null ? `Roll ${formatRoll(profile.roll_number)}` : null,
    capitalize(profile.gender),
    profile.region,
    profile.username && `@${profile.username}`,
    profile.date_joined ? `joined ${dayMonth(profile.date_joined, true)}` : null,
  ].filter(Boolean);
  const openAlerts = overview.open_alerts?.length ?? 0;
  const aim = profile.target_departments?.[0];
  const guardianName = guardian?.parent_name?.trim();

  return (
    <section aria-label="Student summary" className="relative mb-[18px] overflow-hidden rounded-[24px] bg-navy px-5 py-5 text-white sm:px-7 sm:py-[26px]">
      {/* Artboard `.band::after`: a soft slate glow at the top right. */}
      <span aria-hidden="true" className="pointer-events-none absolute -right-[90px] -top-[140px] h-[420px] w-[420px] rounded-full bg-[radial-gradient(closest-side,rgba(78,125,150,.5),rgba(78,125,150,0))]" />
      <div className="relative flex flex-wrap items-center gap-[22px]">
        <div aria-hidden className="grid h-[88px] w-[88px] flex-none place-items-center rounded-[28px] bg-mist font-display text-[30px] font-extrabold tracking-[-0.02em] text-ink ring-[5px] ring-navy-2 dark:ring-primary">
          {initials(profile.first_name, profile.last_name, profile.username)}
        </div>
        <div className="min-w-0 flex-[1_1_340px]">
          <p className="mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-on-navy-muted">
            <Link to="/students" className="text-on-navy hover:text-white hover:underline">Students</Link> / {gradeSection}
          </p>
          <h1 className="break-words font-display text-[38px] font-extrabold leading-[1.05] tracking-[-0.03em] text-white">{name}</h1>
          <p className="mt-2 text-sm text-on-navy-muted">{meta.join(' · ')}</p>
          <div className="mt-3.5 flex flex-wrap gap-2">
            {openAlerts > 0 && (
              <span className="inline-flex min-h-[30px] items-center gap-[7px] rounded-full bg-tag-bg px-3 text-[13px] font-medium text-tag-text">
                <Bell size={15} strokeWidth={1.8} aria-hidden="true" />
                {openAlerts} open alert{openAlerts === 1 ? '' : 's'}
              </span>
            )}
            <span className={TAG}>
              <span aria-hidden="true" className={`h-2 w-2 flex-none rounded-full ${DOT[kpis.engagement_dot] ?? 'bg-line-hover'}`} />
              Engagement · {ENGAGEMENT_LABEL[kpis.engagement_dot] ?? kpis.engagement_dot}
            </span>
            {!teacherView && (
              <span className={TAG}>
                <span aria-hidden="true" className={`h-2 w-2 flex-none rounded-full ${DOT[kpis.wellbeing_dot] ?? 'bg-line-hover'}`} />
                Wellbeing · {WELLBEING_LABEL[kpis.wellbeing_dot] ?? kpis.wellbeing_dot}
              </span>
            )}
            {kpis.avg_score !== null && kpis.avg_score !== undefined && (
              <span className={TAG}>Avg <span className="font-mono font-medium">{kpis.avg_score.toFixed(1)}%</span></span>
            )}
            {remediationCycle != null && <span className={TAG}>Remediation · cycle {remediationCycle}</span>}
            {aim && <span className={TAG}>Aims for {aim}</span>}
          </div>
        </div>
        {overview.risk && <RiskRing score={overview.risk.score} band={overview.risk.band} />}
      </div>

      <div className="relative mt-[22px] flex flex-wrap items-center gap-2.5 border-t border-navy-3 pt-[18px]">
        {guardianName && (
          <span className="flex min-w-0 flex-[1_1_300px] items-center gap-2.5 text-[13px] text-on-navy">
            <span aria-hidden="true" className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[12px] bg-mist text-xs font-semibold text-ink">
              {initials(guardianName.split(/\s+/)[0] ?? '', guardianName.split(/\s+/).slice(-1)[0] ?? '', guardianName)}
            </span>
            <span className="min-w-0">
              Guardian <b className="font-semibold text-white">{guardianName}</b>
              {guardian?.telegram_linked ? ' · Telegram linked' : ''}
            </span>
          </span>
        )}
        <span className="flex-1" />
        {onAddNote && (
          <Button variant="on-navy" icon={<Pencil className={BAND_ICON} strokeWidth={1.8} aria-hidden="true" />} onClick={onAddNote}>Add note</Button>
        )}
        {onExportReport && (
          <Button variant="on-navy" loading={exporting}
            icon={<Download className={BAND_ICON} strokeWidth={1.8} aria-hidden="true" />} onClick={onExportReport}>
            Weekly report (PDF)
          </Button>
        )}
        {onMessageStudent && (
          <Button variant="ghost" icon={<Mail className={BAND_ICON} strokeWidth={1.8} aria-hidden="true" />} onClick={onMessageStudent}>Message student</Button>
        )}
      </div>
    </section>
  );
}
